import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EmeraldModalModule } from '@emerald-angular/modal';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { TimeSheetData } from '@app/shared/interfaces/vendor-management-model';
import { dropdownValues } from '@app/shared/interfaces/common.model';

@Component({
  selector: 'app-vendor-timesheet-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    EmeraldModalModule,
    EmeraldDropdownModule,
    EmeraldButtonModule,
  ],
  templateUrl: './vendor-timesheet-filter.component.html',
  styleUrl: './vendor-timesheet-filter.component.scss',
})
export class VendorTimesheetFilterComponent implements OnInit, OnChanges {
  VendorFilterForm!: FormGroup;
  openForm: boolean = true;
  projectOptions: dropdownValues[] = [];
  reportingManagerOptions: dropdownValues[] = [];
  dntLeaderOptions: dropdownValues[] = [];
  employeeStatusOptions: dropdownValues[] = [];
  timesheetStatusOptions: dropdownValues[] = [
    { label: 'All', value: 'All' },
    { label: 'Pending Submission', value: 'Pending Submission' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Pending Approval', value: 'Pending Approval' },
  ];
  @Input() vendorTimesheetData: TimeSheetData[] = [];
  @Output() applyFilter = new EventEmitter<any>();
  @Output() applyFilterCriteria = new EventEmitter<{project?: string, reportingManager?: string, status?: string, dntLeader?: string, employeeStatus?: string}>();
  @Output() resetFilter = new EventEmitter<any>();

  constructor() {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(): void {
    this.updateReportingManagerOptions();
  }

  private updateReportingManagerOptions(): void {
    if (!this.vendorTimesheetData?.length) {
      this.reportingManagerOptions = [{ label: 'All', value: 'All' }];
      this.projectOptions = [{ label: 'All', value: 'All' }];
      this.dntLeaderOptions = [{ label: 'All', value: 'All' }];
      this.employeeStatusOptions = [{ label: 'All', value: 'All' }];
      return;
    }

    const getUniqueOptions = (key: keyof TimeSheetData) =>
      Array.from(
        new Set(
          this.vendorTimesheetData
            ?.map((item) => item[key])
            ?.filter((val) => !!val && typeof val === 'string' && val.trim()),
        ),
      ).map((val) => ({ label: val as string, value: val as string }));

    const reportingManagers = getUniqueOptions('reportingManager');
    this.reportingManagerOptions = [
      { label: 'All', value: 'All' },
      ...reportingManagers
    ];
    
    const projects = getUniqueOptions('projectName');
    this.projectOptions = [
      { label: 'All', value: 'All' },
      ...projects
    ];
    
    const dntLeaders = getUniqueOptions('l3ManagerName');
    this.dntLeaderOptions = [
      { label: 'All', value: 'All' },
      ...dntLeaders
    ];
    
    // Normalize employee status options
    const statusValues = getUniqueOptions('employeeStatus');
    const normalizedStatuses = new Set<string>();
    statusValues.forEach((item) => {
      if (item.value.toLowerCase() === 'inactive') {
        normalizedStatuses.add('InActive');
      } else if (item.value.toLowerCase() === 'active') {
        normalizedStatuses.add('Active');
      } else {
        normalizedStatuses.add(item.value);
      }
    });
    
    const normalizedStatusOptions = Array.from(normalizedStatuses).map((status) => ({
      label: status,
      value: status
    }));
    
    this.employeeStatusOptions = [
      { label: 'All', value: 'All' },
      ...normalizedStatusOptions
    ];
  }

  initializeForm(): void {
    this.VendorFilterForm = new FormGroup({
      project: new FormControl(null),
      reportingManager: new FormControl(null),
      dntLeader: new FormControl(null),
      employeeStatus: new FormControl(null),
      status: new FormControl(null),
    });
  }

  reset(): void {
    this.VendorFilterForm.reset();
    this.openForm = false;
    setTimeout(() => {
      this.openForm = true;
    });
    this.resetFilter.emit(this.vendorTimesheetData);
  }

  apply(): void {
    const { project, reportingManager, dntLeader, employeeStatus, status } =
      this.VendorFilterForm.value;

    let filteredData = [...this.vendorTimesheetData];
    
    if (project?.value && project.value !== 'All') {
      filteredData = filteredData.filter(item => item.projectName === project.value);
    }

    if (reportingManager?.value && reportingManager.value !== 'All') {
      filteredData = filteredData.filter(item => item.reportingManager === reportingManager.value);
    }

    if (dntLeader?.value && dntLeader.value !== 'All') {
      filteredData = filteredData.filter(item => item.l3ManagerName === dntLeader.value);
    }

    if (employeeStatus?.value && employeeStatus.value !== 'All') {
      filteredData = filteredData.filter(item => {
        if (employeeStatus.value.toLowerCase() === 'inactive') {
          return item.employeeStatus?.toLowerCase() === 'inactive';
        }
        return item.employeeStatus === employeeStatus.value;
      });
    }

    if (status?.value && status.value !== 'All') {
      filteredData = filteredData.filter(item => 
        (item.status?.toLowerCase?.() ?? '') === (status.value?.toLowerCase?.() ?? '')
      );
    }

    const criteria = {
      project: project?.value === 'All' ? undefined : project?.value,
      reportingManager: reportingManager?.value === 'All' ? undefined : reportingManager?.value,
      dntLeader: dntLeader?.value === 'All' ? undefined : dntLeader?.value,
      employeeStatus: employeeStatus?.value === 'All' ? undefined : employeeStatus?.value,
      status: status?.value === 'All' ? 'all' : status?.value
    };

    this.applyFilter.emit(filteredData);
    this.applyFilterCriteria.emit(criteria);
  }
}
